import React, { useState } from 'react';
import { AxiosError } from "axios";

import { ITransactionUserSummary } from "@eagerdog/interfaces";

import { apiService } from "../../services";

import Checkbox from "../../components/Checkbox/Checkbox";
import Input from "../../components/Input/Input";
import Button from "../../components/Button/Button";
import { toast } from "../../components/Toast/ToastManager";

import "./Refund.scss";

interface IProps {
  transaction: ITransactionUserSummary,
  type: "event" | "course" | "store",
  clubId?: string,
  onRefund(): void,
  onCancel(): void
}

const Refund: React.FC<IProps> = (props) => {
  const [isRefunding, setIsRefunding] = useState<boolean>(false);
  const [includeStripeFees, setIncludeStripeFees] = useState<boolean>(false);
  const [useCustomAmount, setUseCustomAmount] = useState<boolean>(false);
  const [customAmount, setCustomAmount] = useState<string>("");

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: props.transaction.amounts.currency.toUpperCase() || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  // Use the actual processed fee if it's a valid positive number (set by the Stripe webhook).
  // Fall back to the calculated estimate when the processed value is 0 or negative
  // (e.g. destination charges where the webhook formula can produce a negative result).
  const processedFee = props.transaction.amounts.processed_processing_fee ?? 0;
  const calculatedFee = props.transaction.amounts.calculated_processing_fee ?? 0;
  const stripeFeeCents = processedFee > 0 ? processedFee : calculatedFee;

  // The full charge is the ceiling. EagerDog's fee is part of the charge and is reversed
  // automatically by Stripe (refund_application_fee: true), so it does NOT reduce eligibility.
  // The ONLY thing held back is the Stripe processing fee ($1.54) when not included.
  //   Without Stripe fees: customer gets totalCharge - stripeFee (e.g. $33.39 - $1.54 = $31.85)
  //   With Stripe fees:    customer gets totalCharge (e.g. $33.39), club absorbs $1.54
  const totalChargeCents = props.transaction.amounts.calculated_total_amount;
  const alreadyRefundedCents = props.transaction.amounts.refunded_amount ?? 0;

  const remainingRefundableCents = Math.max(0, totalChargeCents - alreadyRefundedCents);
  const remainingWithoutStripeCents = Math.max(0, (totalChargeCents - stripeFeeCents) - alreadyRefundedCents);

  // Courses have all fees paid by the club, so the Stripe processing fee is
  // always refunded to the customer (never held back) and isn't surfaced.
  const isCourse = props.type === "course";

  const stripeIncrementalCents = stripeFeeCents;
  const canIncludeStripeFees = !isCourse && stripeFeeCents > 0 && remainingRefundableCents > 0;
  const effectiveIncludeStripeFees = isCourse || (includeStripeFees && canIncludeStripeFees);

  const displayedRemainingCents = isCourse
    ? remainingRefundableCents
    : (!useCustomAmount && effectiveIncludeStripeFees) ? remainingRefundableCents : remainingWithoutStripeCents;
  const defaultRefundAmount = displayedRemainingCents / 100;

  // Custom amounts are entered as the ENTRY-FEE (club) portion. When the exhibitor
  // paid the Eager Dog fee on top of the entry, the proportional share of that fee is
  // added to what the exhibitor receives — the same top-down formula the refund-request
  // approval uses (entry + proportional ED fee), so a "$5 refund" costs the club $5
  // whichever path it goes through. Courses and club-absorbed-fee transactions have no
  // fee in the exhibitor's charge, so the custom amount is the gross there.
  const amounts = props.transaction.amounts;
  const entryAmountCents = Math.max(0, (amounts.calculated_base_amount ?? 0) - (amounts.calculated_discount_amount ?? 0));
  const edFeeTotalCents = (amounts.calculated_application_fee ?? 0) + (amounts.calculated_processing_tax ?? 0);
  const edFeeRefundedCents = amounts.refunded_application_fee ?? 0;
  const edFeeRemainingCents = Math.max(0, edFeeTotalCents - edFeeRefundedCents);
  const feesChargedToCustomer = props.type === "event" && amounts.platform_fees_by !== "club" && edFeeTotalCents > 0 && entryAmountCents > 0;

  const customEntryCents = Math.round((parseFloat(customAmount) || 0) * 100);
  const customEdPortionCents = feesChargedToCustomer
    ? Math.max(0, Math.min(
        edFeeRemainingCents,
        Math.round((customEntryCents / entryAmountCents) * edFeeTotalCents),
        remainingWithoutStripeCents - customEntryCents
      ))
    : 0;
  const customGrossCents = customEntryCents + customEdPortionCents;

  // maxCustomAmount caps the custom-amount input (never includes Stripe fee — club absorbs that separately).
  // With fees charged to the exhibitor it is the remaining ENTRY portion (the ED fee rides on top).
  const remainingEntryCents = Math.max(0, entryAmountCents - Math.max(0, alreadyRefundedCents - edFeeRefundedCents));
  const maxCustomAmount = (isCourse
    ? remainingRefundableCents
    : feesChargedToCustomer
      ? Math.min(remainingEntryCents, remainingWithoutStripeCents)
      : remainingWithoutStripeCents) / 100;
  // maxValidationAmount is the true ceiling for the GROSS amount sent to the API.
  const maxValidationAmount = (!useCustomAmount && effectiveIncludeStripeFees)
    ? remainingRefundableCents / 100
    : (isCourse ? remainingRefundableCents : remainingWithoutStripeCents) / 100;

  const refundAllowed = remainingRefundableCents > 0;

  const refund = () => {
    setIsRefunding(true);

    const amountRefunded: number = useCustomAmount
      ? customGrossCents / 100
      : defaultRefundAmount;
    const customTooLarge = useCustomAmount && customEntryCents > Math.round(maxCustomAmount * 100);

    if (amountRefunded <= 0 || amountRefunded > maxValidationAmount || customTooLarge || !refundAllowed) {
      toast.show({
        title: "Refund Transaction",
        content: "Refund failed because you're attempting to refund more money than the total transaction ("+ formatter.format(amountRefunded) +")",
        duration: 10000,
        type: "fail"
      });

      setIsRefunding(false);
    } else {
      if (props.type === "event") {
        const refundDto = useCustomAmount && feesChargedToCustomer
          ? { amount: amountRefunded, entry_amount: customEntryCents / 100 }
          : { amount: amountRefunded };
        apiService.refundEventTransaction(props.transaction.event_id!, props.transaction._id, refundDto).then(() => {
          toast.show({
            title: "Refund Transaction",
            content: "Transaction has been refunded.",
            duration: 10000,
            type: "success"
          });

          setIsRefunding(false);
          props.onRefund();
        }).catch((e: AxiosError) => {
          toast.show({
            title: "Refund Transaction",
            content: "Unable to refund transaction",
            duration: 10000,
            errorDetails: e,
            type: "fail"
          });
        }).finally(() => {
          setIsRefunding(false);
        });
      } else if (props.type === "course") {
        apiService.refundCourseTransaction(props.transaction.course_id!, props.transaction._id, { amount: amountRefunded }).then(() => {
          toast.show({
            title: "Refund Transaction",
            content: "Transaction has been refunded.",
            duration: 10000,
            type: "success"
          });

          setIsRefunding(false);
          props.onRefund();
        }).catch((e: AxiosError) => {
          toast.show({
            title: "Refund Transaction",
            content: "Unable to refund transaction",
            duration: 10000,
            errorDetails: e,
            type: "fail"
          });
        }).finally(() => {
          setIsRefunding(false);
        });
      } else if (props.type === "store") {
        if (!props.clubId) {
          toast.show({
            title: "Refund Transaction",
            content: "Club ID is required for store refunds",
            duration: 10000,
            type: "fail"
          });
          setIsRefunding(false);
          return;
        }
        
        apiService.refundStoreTransaction(props.clubId, props.transaction._id, { amount: amountRefunded }).then(() => {
          toast.show({
            title: "Refund Transaction",
            content: "Transaction has been refunded.",
            duration: 10000,
            type: "success"
          });

          setIsRefunding(false);
          props.onRefund();
        }).catch((e: AxiosError) => {
          toast.show({
            title: "Refund Transaction",
            content: "Unable to refund transaction",
            duration: 10000,
            errorDetails: e,
            type: "fail"
          });
        }).finally(() => {
          setIsRefunding(false);
        });
      }
    }
  }

  return(
    <form className="Refund" onSubmit={(e) => { e.preventDefault(); if (!isRefunding) { refund(); } }}>
      <div className="title">
        <span>Refund</span>
        <span>{props.transaction.user.display_name}</span>
      </div>

      {refundAllowed && canIncludeStripeFees && !useCustomAmount && (
        <div className="topOptions">
          <Checkbox id="includeStripeFees" onChange={() => { setIncludeStripeFees(!includeStripeFees); }} value={effectiveIncludeStripeFees} label={`Include Stripe Fees (+${formatter.format(stripeIncrementalCents / 100)})`} />
          {effectiveIncludeStripeFees && (
            <div className="stripeFeeInfo">
              <div className="infoIcon"><div className="icon info"></div></div>
              <div className="infoContent">Stripe's processing fee will be refunded from the club's account.</div>
            </div>
          )}
        </div>
      )}

      {/* Transaction Summary */}
      <div className="transactionSummary">
        <div className="summaryTitle">Transaction Summary</div>

        <div className="summaryItem">
          <span>Total Transaction Amount:</span>
          <span>{formatter.format(props.transaction.amounts.calculated_total_amount / 100)}</span>
        </div>

        {props.transaction.amounts.calculated_discount_amount > 0 && (
          <div className="summaryItem">
            <span>Discount Applied:</span>
            <span>-{formatter.format(props.transaction.amounts.calculated_discount_amount / 100)}</span>
          </div>
        )}

        {!effectiveIncludeStripeFees && !useCustomAmount && stripeFeeCents > 0 && (
          <div className="summaryItem muted">
            <span>Stripe Fees (not included in refund):</span>
            <span>-{formatter.format(stripeFeeCents / 100)}</span>
          </div>
        )}

        {alreadyRefundedCents > 0 && (
          <div className="summaryItem refundedRow">
            <span>Previously Refunded:</span>
            <span>-{formatter.format(alreadyRefundedCents / 100)}</span>
          </div>
        )}

        <div className="summaryItem remaining">
          <span>Remaining Amount Eligible for Refund:</span>
          <span>{formatter.format(displayedRemainingCents / 100)}</span>
        </div>
      </div>

      {(() => {
        const edFeeCents = (props.transaction.amounts.processed_application_fee || props.transaction.amounts.calculated_application_fee) + props.transaction.amounts.calculated_processing_tax;
        return edFeeCents > 0 && displayedRemainingCents > 0 && !useCustomAmount ? (
          <div className="stripeFeeInfo">
            <div className="infoIcon"><div className="icon info"></div></div>
            <div className="infoContent">Eager Dog covers <strong>{formatter.format(edFeeCents / 100)}</strong> in platform fees — credited back proportionally with each refund.</div>
          </div>
        ) : null;
      })()}

      <div className="content">
        {refundAllowed && (
          <>
            <div className="checkboxList">
              <div className="listLabel">Custom Amount?</div>
              <div className="listWrap">
                <Checkbox id="useCustomAmount" onChange={() => { const next = !useCustomAmount; setUseCustomAmount(next); if (next) { setIncludeStripeFees(false); } }} value={useCustomAmount} label={"Enter a specific amount"} />
              </div>
            </div>
            {useCustomAmount && (
              <div className="customAmount">
                <Input
                  disabled={!refundAllowed}
                  type="number"
                  min="0.01"
                  max={maxCustomAmount}
                  step=".01"
                  label={(feesChargedToCustomer ? "Entry Fee Amount to Refund (" : "Custom Amount (") + props.transaction.amounts.currency.toUpperCase() + ")"}
                  onChange={(e: any) => { setCustomAmount(e.target.value); }}
                  placeholder="0.00"
                />
                {feesChargedToCustomer && customEntryCents > 0 && (
                  <div className="transactionSummary">
                    <div className="summaryItem">
                      <span>Entry fee refunded by the club:</span>
                      <span>{formatter.format(customEntryCents / 100)}</span>
                    </div>
                    <div className="summaryItem">
                      <span>Eager Dog fee returned by Eager Dog:</span>
                      <span>+{formatter.format(customEdPortionCents / 100)}</span>
                    </div>
                    <div className="summaryItem remaining">
                      <span>Exhibitor receives:</span>
                      <span>{formatter.format(customGrossCents / 100)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {props.transaction.amounts.payment_method_type === "bank_account" && (
        <div className="stripeFeeInfo">
          <div className="infoIcon"><div className="icon info"></div></div>
          <div className="infoContent">This payment was made via bank account. Refunds to bank accounts take <strong>3 business days</strong> to process.</div>
        </div>
      )}

      {refundAllowed && (
        <div className="actions">
          {!isRefunding && <Button onClick={() => { props.onCancel(); }} className="cancel" type="button">Cancel</Button>}
          <Button disabled={!refundAllowed} isLoading={isRefunding} type="submit">Refund ({formatter.format(useCustomAmount ? customGrossCents / 100 : defaultRefundAmount)})</Button>
        </div>
      )}
      {!refundAllowed && (
        <div className="actions">
          <Button onClick={() => { props.onCancel(); }} className="cancel" type="button">Close</Button>
        </div>
      )}
    </form>
  );
}

export default Refund;